import {
  type FieldValues,
  type FormItemWrapperProps,
  type UseControllerProps,
  useCustomController,
} from '@faceup/form'
import { defineMessages, useIntl } from '@faceup/localization'
import { FormItem, InputMatrix } from '@faceup/ui'
import { Checkbox, Radio } from '@faceup/ui-base'
import { type AnyObject, type ArraySchema, yup } from '@faceup/validation'

const messages = defineMessages('followUp.components.survey.surveyMatrixChoice', [
  { id: 'required', defaultMessage: 'This question requires one response per row.' },
] as const)

type SurveyMatrixData = {
  isMultipleSelectionAllowed: boolean
  options: {
    id: string
  }[]
  questions: {
    id: string
  }[]
}

const useSchema = () => {
  const { formatMessage } = useIntl()
  const getSchema = (data: SurveyMatrixData, required: boolean) =>
    yup.object(
      data.questions.reduce<Record<string, ArraySchema<string[] | undefined, AnyObject, '', ''>>>(
        (acc, question) => ({
          ...acc,
          [question.id]: required
            ? yup
                .array()
                .of(yup.string().required())
                .min(1)
                .required(formatMessage(messages.required))
            : yup.array().of(yup.string().required()),
        }),
        {}
      )
    )

  return getSchema
}

type SurveyMatrixDataWithLabel = {
  isMultipleSelectionAllowed: boolean
  options: {
    id: string
    label: string
  }[]
  questions: {
    id: string
    label: string
  }[]
}

type Value = Record<string, string[]>

type SurveyMatrixChoiceProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<FormItemWrapperProps, 'value' | 'defaultValue' | 'placeholder'> &
  SurveyMatrixDataWithLabel & {
    placeholder?: string | Promise<string>
    loading?: boolean
  }

export const SurveyMatrixChoice = <T extends FieldValues>({
  options,
  questions,
  isMultipleSelectionAllowed,
  ...props
}: SurveyMatrixChoiceProps<T>) => {
  const {
    formItemProps,
    inputProps: { onChange: fieldOnChange, value },
  } = useCustomController(props)

  const typedValue = value as Value

  return (
    <FormItem notClickableLabel {...formItemProps}>
      <InputMatrix
        columns={options.map(option => option.label)}
        rows={questions.map(question => question.label)}
        renderCell={(rowIndex, columnIndex) => {
          const question = questions[rowIndex]
          const option = options[columnIndex]
          if (!question || !option) {
            return ''
          }
          const currentValue = (typedValue?.[question.id] ?? []).includes(option.id)
          const Component = isMultipleSelectionAllowed ? Checkbox : Radio
          return (
            <Component
              checked={Boolean(currentValue)}
              onChange={e => {
                const question = questions[rowIndex]
                const option = options[columnIndex]
                if (!question || !option) {
                  return
                }

                const questionId = question.id
                const optionId = option.id

                if (!isMultipleSelectionAllowed) {
                  const newValue = {
                    ...typedValue,
                    [questionId]: e.target.checked ? [optionId] : [],
                  }
                  fieldOnChange(newValue)
                  return
                }

                const currentValues = typedValue?.[questionId] ?? []
                const newValue = {
                  ...typedValue,
                  [questionId]: e.target.checked
                    ? [...currentValues, optionId]
                    : currentValues.filter(v => v !== optionId),
                }
                fieldOnChange(newValue)
              }}
            />
          )
        }}
      />
    </FormItem>
  )
}

SurveyMatrixChoice.useSchema = useSchema
